/** BNT Asistente — ventana compacta (fase 1: sin extensión de navegador). */

import { isEmbeddedBrowserContext } from "@/lib/embeddedBrowserContext";
import {
  BNT_ASISTENTE_WINDOW_NAME,
  BNT_MSG_SET_TAB,
  markAssistantPopupActive,
} from "@/modules/asistente/bntAsistenteMessaging";

export const BNT_ASISTENTE_PATH = "/asistente";

/** @type {Window | null} */
let assistantPopupRef = null;

/** @type {((opts?: { tab?: string }) => void) | null} */
let integratedPanelOpener = null;

export function isBntAsistentePath(pathname) {
  const path = String(pathname || "").split("?")[0];
  return path === BNT_ASISTENTE_PATH || path.startsWith(`${BNT_ASISTENTE_PATH}/`);
}

/** En Cursor / Simple Browser el popup abre una hoja completa; preferir panel integrado. */
export function shouldPreferIntegratedAssistantPanel() {
  return isEmbeddedBrowserContext();
}

export function registerBntAsistenteIntegratedOpener(fn) {
  integratedPanelOpener = typeof fn === "function" ? fn : null;
}

function buildAssistantUrl({ tab } = {}) {
  const params = new URLSearchParams({ compact: "1" });
  if (tab) params.set("tab", tab);
  return `${window.location.origin}${BNT_ASISTENTE_PATH}?${params.toString()}`;
}

function buildPopupFeatures() {
  const width = 440;
  const height = Math.min(760, window.screen.availHeight - 48);
  const left = Math.max(0, (window.screen.availWidth || width) - width - 16);
  const top = 16;
  return [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    "resizable=yes",
    "scrollbars=yes",
    "noopener=no",
  ].join(",");
}

function notifyPopupTab(tab) {
  if (!assistantPopupRef || assistantPopupRef.closed || !tab) return;
  try {
    assistantPopupRef.postMessage({ type: BNT_MSG_SET_TAB, tab }, window.location.origin);
  } catch {
    // ignore
  }
}

function openIntegratedPanel({ tab } = {}) {
  if (!integratedPanelOpener) return false;
  integratedPanelOpener({ tab });
  return true;
}

/** Reutiliza ventana popup existente si sigue abierta. */
export function getAssistantPopupRef() {
  if (assistantPopupRef && !assistantPopupRef.closed) return assistantPopupRef;
  assistantPopupRef = null;
  return null;
}

export function registerAssistantPopup(popup) {
  assistantPopupRef = popup || null;
  if (popup && !popup.closed) {
    markAssistantPopupActive(true);
  }
}

export function clearAssistantPopupRef() {
  assistantPopupRef = null;
  markAssistantPopupActive(false);
}

/** Abre o restaura la ventana compacta del asistente (popup lateral). */
export function openBntAsistenteWindow({ tab } = {}) {
  if (typeof window === "undefined") return null;

  const existing = getAssistantPopupRef();
  if (existing) {
    if (tab) notifyPopupTab(tab);
    existing.focus();
    return existing;
  }

  const url = buildAssistantUrl({ tab });
  const popup = window.open(url, BNT_ASISTENTE_WINDOW_NAME, buildPopupFeatures());
  if (popup) {
    registerAssistantPopup(popup);
    popup.focus();
  }
  return popup;
}

/**
 * Abre el asistente: panel integrado en Cursor/IDE; ventana flotante en navegador externo.
 * @returns {"integrated"|"popup"|"none"}
 */
export function openBntAsistente({ tab } = {}) {
  if (typeof window === "undefined") return "none";

  if (shouldPreferIntegratedAssistantPanel()) {
    return openIntegratedPanel({ tab }) ? "integrated" : "none";
  }

  const popup = openBntAsistenteWindow({ tab });
  return popup ? "popup" : "none";
}
